import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageMeta from '../components/common/PageMeta';
import ComponentCard from '../components/common/ComponentCard';
import PageBreadcrumb from '../components/common/PageBreadCrumb';
import Spinner from '../components/ui/Spinner';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import Alert from '../components/ui/alert/Alert';
import { subscriptionService, SubscriptionTier } from '../services/SubscriptionService';
import SubscriptionTierCard from '../components/subscription/SubscriptionTierCard';
import { userStateService } from '../services/UserStateService';
import TiersStateService from '../services/TiersStateService';

interface TierInfo {
    id: SubscriptionTier;
    name: string;
    description: string;
    price: string;
    features: string[];
    isPopular?: boolean;
}

export default function Subscription() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [currentTier, setCurrentTier] = useState<SubscriptionTier | null>(null);
    const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [markedForCancel, setMarkedForCancel] = useState(false);
    const [cancelAtDate, setCancelAtDate] = useState<Date | null>(null);

    const user_id = userStateService.getState().id;
    const email = userStateService.getState().email;

    const tiers: TierInfo[] = [
        {
            id: 'Basic',
            name: 'Basic Tier',
            description: 'Essential features for individuals',
            price: '$15/month',
            isPopular: false,
            features: [
                //'Limited API access',
                'Standard support',
                '100 pages per month',
                //'Standard processing speed'
            ]
        },
        {
            id: 'Pro',
            name: 'Pro Tier',
            description: 'Additional conversions for professionals',
            price: '$30/month',
            isPopular: true,
            features: [
                //'Full API access',
                'Standard support',
                '300 pages per month',
                //'Enhanced processing speed',
                //'Advanced analytics'
            ]
        },
        {
            id: 'ProPlus',
            name: 'Pro Plus Tier',
            description: 'High conversions for teams',
            price: '$60/month',
            isPopular: false,
            features: [
                //'Unlimited API access',
                //'24/7 Dedicated support',
                'Priority support',
                '900 pages per month',
                //'Unlimited files',
                //'Fastest processing speed',
                //'Full analytics suite',
                //'Custom integrations'
            ]
        }
    ];

    useEffect(() => {
        if (!user_id) {
            navigate('/signin');
            return;
        }

        const fetchUserSubscription = async () => {
            setLoading(true);
            try {
                const subscriptionStatus = await subscriptionService.getUserSubscriptionStatus(user_id);

                const newCancelAtDate = subscriptionStatus.cancel_at 
                    ? new Date(subscriptionStatus.cancel_at) 
                    : null;

                setIsSubscribed(subscriptionStatus.isSubscribed);
                setCurrentTier(subscriptionStatus.tier_code || null);
                setMarkedForCancel(subscriptionStatus.marked_for_cancel || false);
                setCancelAtDate(newCancelAtDate);

                // Set the initial selected tier based on current subscription
                if (subscriptionStatus.isSubscribed && subscriptionStatus.tier_code) {
                    setSelectedTier(subscriptionStatus.tier_code);
                }
            } catch (err) {
                setError('Failed to load subscription information');
                console.error('Error fetching subscription status:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchUserSubscription();
    }, [user_id, navigate]);

    const handleSelectTier = (tier: SubscriptionTier) => {
        setSelectedTier(tier);
    };

    const handleSubscribe = async () => {
        if (!user_id || !selectedTier || !email) return;

        setProcessing(true);
        setError(null);

        try {
            const response = await subscriptionService.createSubscription(user_id, email, selectedTier);

            if (response.success && response.url) {
                // Navigate to the payment URL
                window.location.href = response.url;
            } else {
                setError(response.message || 'Failed to create subscription');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.error('Error creating subscription:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleChangeSubscription = async () => {
        if (!user_id || !selectedTier || selectedTier === currentTier) return;

        setProcessing(true);
        setError(null);

        try {
            const response = await subscriptionService.changeSubscription(user_id, selectedTier);

            if (response.success) {
                if (response.url) {
                    window.location.href = response.url;
                } else {
                    setSuccess('Subscription updated successfully');
                    setIsSubscribed(true);
                    setCurrentTier(selectedTier);
                }
            } else {
                setError(response.message || 'Failed to change subscription');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.error('Error changing subscription:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!user_id) return;

        setProcessing(true);
        setError(null);
        setShowCancelDialog(false);

        try {
            const response = await subscriptionService.cancelSubscription(user_id);

            if (response.success) {
                setSuccess('Your subscription will cancel at the end of your current billing period');
                
                // Instead of clearing subscription state immediately, we need to refresh it
                // to get the correct marked_for_cancel and cancel_at values
                try {
                    const subscriptionStatus = await subscriptionService.getUserSubscriptionStatus(user_id);
                    
                    const newCancelAtDate = subscriptionStatus.cancel_at 
                    ? new Date(subscriptionStatus.cancel_at) 
                    : null;

                    // Update state with the latest subscription data
                    setIsSubscribed(subscriptionStatus.isSubscribed);
                    setCurrentTier(subscriptionStatus.tier_code || null);
                    setMarkedForCancel(subscriptionStatus.marked_for_cancel || false);
                    setCancelAtDate(newCancelAtDate);
                    
                    // Keep the current tier selected
                    if (subscriptionStatus.isSubscribed && subscriptionStatus.tier_code) {
                        setSelectedTier(subscriptionStatus.tier_code);
                    }
                } catch (err) {
                    console.error('Error refreshing subscription status:', err);
                }
            } else {
                setError(response.message || 'Failed to cancel subscription');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.error('Error cancelling subscription:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleUndoCancellation = async () => {
        if (!user_id) return;

        setProcessing(true);
        setError(null);

        try {
            const response = await subscriptionService.undoCancelSubscription(user_id);

            if (response.success) {
                setSuccess('Your subscription cancellation has been reversed.');
                
                // Refresh subscription state from the server after undoing cancellation
                try {
                    const subscriptionStatus = await subscriptionService.getUserSubscriptionStatus(user_id);
                    
                    const newCancelAtDate = subscriptionStatus.cancel_at 
                    ? new Date(subscriptionStatus.cancel_at) 
                    : null;

                    // Update all subscription state with the latest data
                    setIsSubscribed(subscriptionStatus.isSubscribed);
                    setCurrentTier(subscriptionStatus.tier_code || null);
                    setMarkedForCancel(subscriptionStatus.marked_for_cancel || false);
                    setCancelAtDate(newCancelAtDate);
                    
                    // Keep the current tier selected
                    if (subscriptionStatus.isSubscribed && subscriptionStatus.tier_code) {
                        setSelectedTier(subscriptionStatus.tier_code);
                    }
                } catch (err) {
                    console.error('Error refreshing subscription status:', err);
                }
            } else {
                setError(response.message || 'Failed to undo cancellation');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.error('Error undoing cancellation:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleOpenCustomerPortal = async () => {
        if (!user_id) return;
        
        setProcessing(true);
        setError(null);
        
        try {
            const response = await subscriptionService.createCustomerPortalSession(
                user_id,
                window.location.href
            );
            
            if (response.success && response.url) {
                window.location.href = response.url;
            } else {
                setError(response.message || 'Failed to open customer portal');
            }
        } catch (err) {
            setError('An unexpected error occurred');
            console.error('Error opening customer portal:', err);
        } finally {
            setProcessing(false);
        }
    };

    const getSubscriptionChangeButtonText = () => {
        if (!currentTier || !selectedTier) return "Update Subscription";

        // Tier hierarchy for comparison
        const tierHierarchy = {
            'Basic': 2,
            'Pro': 3,
            'ProPlus': 4
        };

        if (tierHierarchy[selectedTier as keyof typeof tierHierarchy] > tierHierarchy[currentTier as keyof typeof tierHierarchy]) {
            return "Upgrade Subscription";
        } else if (tierHierarchy[selectedTier as keyof typeof tierHierarchy] < tierHierarchy[currentTier as keyof typeof tierHierarchy]) {
            return "Downgrade Subscription";
        }

        return "Update Subscription";
    };

    const actionButtons = () => {
        if (processing) {
            return (
                <div className="flex justify-center mt-8">
                    <Spinner size="md" message="Processing your request..." />
                </div>
            );
        }

        // If there's a pending cancellation, only show disabled buttons
        if (markedForCancel && cancelAtDate && cancelAtDate > new Date()) {
            return (
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                    {/* <button
                        type="button"
                        disabled
                        className="px-6 py-2 text-sm font-medium text-white bg-gray-400 cursor-not-allowed rounded-lg"
                    >
                        Cancel Subscription
                    </button>
                    <button
                        type="button"
                        disabled
                        className="px-6 py-2 text-sm font-medium text-white bg-gray-400 cursor-not-allowed rounded-lg"
                    >
                        {getSubscriptionChangeButtonText()}
                    </button> */}
                    <button
                        type="button"
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                        onClick={handleOpenCustomerPortal}
                    >
                        <span className="flex items-center">
                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Customer Portal
                        </span>
                    </button>
                </div>
            );
        }

        if (isSubscribed) {
            return (
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-8">
                    {selectedTier === currentTier && (
                        <button
                            type="button"
                            className="px-6 py-2 text-sm font-medium text-white bg-error-500 rounded-lg hover:bg-error-600 dark:bg-error-500 dark:hover:bg-error-600"
                            onClick={() => setShowCancelDialog(true)}
                        >
                            Cancel Subscription
                        </button>
                    )}
                    {selectedTier !== currentTier && (
                        <button
                            type="button"
                            className="px-6 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-600"
                            onClick={handleChangeSubscription}
                        >
                            {getSubscriptionChangeButtonText()}
                        </button>
                    )}
                    <button
                        type="button"
                        className="px-6 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700"
                        onClick={handleOpenCustomerPortal}
                    >
                        <span className="flex items-center">
                            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Customer Portal
                        </span>
                    </button>
                </div>
            );
        } else {
            return (
                <div className="flex justify-center mt-8">
                    <button
                        type="button"
                        disabled={!selectedTier}
                        className={`px-6 py-2 text-sm font-medium text-white rounded-lg ${!selectedTier
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-brand-500 hover:bg-brand-600 dark:bg-brand-500 dark:hover:bg-brand-600'
                            }`}
                        onClick={handleSubscribe}
                    >
                        Subscribe Now
                    </button>
                </div>
            );
        }
    };

    return (
        <>
            <PageMeta
                title="Subscription"
                description="Manage your subscription"
            />
            <PageBreadcrumb pageTitle="Subscription" />

            {error && (
                <div className="mb-6">
                    <Alert
                        variant="error"
                        title="Error"
                        message={error}
                        onClose={() => setError(null)}
                    />
                </div>
            )}

            {success && (
                <div className="mb-6">
                    <Alert
                        variant="success"
                        title="Success"
                        message={success}
                        onClose={() => setSuccess(null)}
                    />
                </div>
            )}

            <ComponentCard title="Subscription Plans" className="mb-8">
                {loading ? (
                    <div className="py-10">
                        <Spinner size="lg" message="Loading subscription information..." />
                    </div>
                ) : (
                    <>
                        {isSubscribed && (
                            <div className="mb-6 bg-green-100 dark:bg-green-800/20 p-4 rounded-md border border-green-200 dark:border-green-800/30">
                                <p className="text-green-800 dark:text-green-300">
                                    You are currently subscribed to the <span className="font-semibold">{tiers.find(t => t.id === currentTier)?.name}</span> plan.
                                </p>
                            </div>
                        )}

                        {markedForCancel && cancelAtDate && cancelAtDate > new Date() && (
                            <div className="mb-6 bg-red-100 dark:bg-red-800/20 p-4 rounded-md border border-red-200 dark:border-red-800/30">
                                <div className="flex justify-between items-center">
                                    <p className="text-red-800 dark:text-red-300">
                                        Your active subscription is due for cancellation on {cancelAtDate.toLocaleDateString()}.
                                    </p>
                                    <button
                                        type="button"
                                        className="text-sm font-medium text-red-800 hover:text-red-900 dark:text-red-300 dark:hover:text-red-200 ml-4 underline"
                                        onClick={handleUndoCancellation}
                                        disabled={processing}
                                    >
                                        Undo Cancellation
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {tiers.map((tier) => (
                                <SubscriptionTierCard
                                    key={tier.id}
                                    tier={tier.id}
                                    details={{
                                        name: tier.name,
                                        description: tier.description,
                                        price: tier.price,
                                        features: tier.features,
                                        isPopular: tier.isPopular
                                    }}
                                    isSelected={selectedTier === tier.id}
                                    isDisabled={processing || (markedForCancel && cancelAtDate != null && new Date(cancelAtDate) > new Date())}
                                    onClick={handleSelectTier}
                                />
                            ))}
                        </div>

                        {actionButtons()}
                    </>
                )}
            </ComponentCard>

            <ConfirmationDialog
                isOpen={showCancelDialog}
                title="Cancel Subscription"
                message="Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period."
                confirmLabel="Yes, Cancel Subscription"
                cancelLabel="No, Keep Subscription"
                onConfirm={handleCancelSubscription}
                onCancel={() => setShowCancelDialog(false)}
            />
        </>
    );
} 